import {
  DayHours,
  DayOfWeek,
  DayType,
  HoursDayType,
  HoursEntry,
  HoursMode,
  OpenRange,
  OpeningHoursResult,
  OpenStatus,
  DateTimePoint,
  ZmanimData,
} from '../types';
import { resolveTimeRule } from './timeUtils';
import { getApplicableDayTypes, YOM_TOV_DAY_TYPES } from './dayTypeDetector';
import { CLOSES_SOON_MINUTES, OPENS_SOON_MINUTES } from '../config/constants';
import { addDaysToDate, dateStringToNoonUtc, isIsraelSummerTime } from './israelTime';

/**
 * Days with no business as usual: if the place would normally be open, we
 * don't guess — status is "call to check". (Plain Shabbos is handled by the
 * day-of-week rule instead: open only if the owner ticked Shabbos.)
 */
const REST_DAY_TYPES: DayType[] = ['yomTov', ...YOM_TOV_DAY_TYPES, 'yomKippur'];

/** Regular hours still apply on these days, with "Hours may differ on special days". */
const MAY_DIFFER_DAY_TYPES: HoursDayType[] = ['fastMinor', 'tishaBav', 'cholHaMoed', 'erevYomTov', 'erevYomKippur', 'erevTishaBav'];

/**
 * "Children" of Erev Yom Tov (decided by Yoel, October 2, 2026): an entry for
 * the child wins; without one, the Erev Yom Tov entries apply — same idea as a
 * specific Yom Tov (Pesach I) winning over the generic 'yomTov'.
 */
const EREV_YOM_TOV_CHILDREN: HoursDayType[] = ['erevYomKippur', 'erevTishaBav'];

/** Zman name for "Shabbos ends" as the local calendar prints it; opening hours only. */
export const SHABBOS_ENDS_ZMAN = 'ShabbosEnds';

/** Empty daysOfWeek means Sunday–Friday; Shabbos only when ticked explicitly. */
const DEFAULT_DAYS: DayOfWeek[] = [0, 1, 2, 3, 4, 5];

/** How far ahead to look for "Opens Sunday 20:00". */
const NEXT_OPENING_SEARCH_DAYS = 14;

// ---------- small date/time helpers ('YYYY-MM-DD' and 'HH:mm' strings) ----------

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export const addDays = addDaysToDate;

export function dayOfWeekOf(date: string): DayOfWeek {
  return dateStringToNoonUtc(date).getUTCDay() as DayOfWeek;
}

// ---------- matching ----------

/** The day fields an hours entry and a shiur session share (shiurTimes.ts reuses these rules). */
export type DayRule = Pick<HoursEntry, 'daysOfWeek' | 'dayTypes' | 'specificDate' | 'season' | 'validFrom' | 'validTo'>;

export function inSeason(entry: DayRule, date: string): boolean {
  if (entry.validFrom && date < entry.validFrom) return false;
  if (entry.validTo && date > entry.validTo) return false;
  if (entry.season === 'summerClock' && !isIsraelSummerTime(date)) return false;
  if (entry.season === 'winterClock' && isIsraelSummerTime(date)) return false;
  return true;
}

/** The special day types an entry names ('weekday' alone means a regular entry). */
function specialTypesOf(entry: DayRule): HoursDayType[] {
  return (entry.dayTypes || []).filter((t) => t !== 'weekday');
}

/**
 * Does the entry give regular hours on this weekday? An entry with no special
 * days: its weekdays, or Sunday–Friday when none are ticked. An entry with
 * special days: only the weekdays ticked as well (it applies on any of them).
 */
export function regularOn(entry: DayRule, dow: DayOfWeek): boolean {
  if (entry.specificDate) return false;
  const days = entry.daysOfWeek || [];
  if (specialTypesOf(entry).length === 0) return (days.length > 0 ? days : DEFAULT_DAYS).includes(dow);
  return days.includes(dow);
}

/**
 * The day types opening hours work with: the minyan day types, plus
 * Erev Yom Kippur / Erev Tisha B'Av (children of Erev Yom Tov). Erev Yom Tov
 * is decided from tomorrow: Hebcal calls Erev Purim Erev Yom Tov but not
 * Hoshana Rabba or the day before Shvi'i shel Pesach; for hours, Erev Yom Tov
 * is the day before Yom Tov, Yom Kippur or Tisha B'Av.
 * Without the next day's zmanim, the minyan day types are used as they are.
 */
export function hoursDayTypes(zmanim: ZmanimData, nextDay?: ZmanimData): HoursDayType[] {
  const types: HoursDayType[] = getApplicableDayTypes(zmanim);
  if (!nextDay) return types;
  const out = types.filter((t) => t !== 'erevYomTov' || nextDay.isYomTov || nextDay.isYomKippur || nextDay.isTishaBav);
  if (nextDay.isYomTov && !zmanim.isYomTov) out.push('erevYomTov');
  if (nextDay.isYomKippur) out.push('erevYomTov', 'erevYomKippur');
  if (nextDay.isTishaBav) out.push('erevYomTov', 'erevTishaBav');
  return Array.from(new Set(out));
}

/**
 * Entries the owner tagged for one of today's special day types. The more
 * specific tag wins: a specific Yom Tov (pesachI) over 'yomTov' (same rule as
 * minyanim), and Erev Yom Kippur / Erev Tisha B'Av over 'erevYomTov'.
 */
export function specialDayMatches<T extends DayRule>(entries: T[], dayTypes: HoursDayType[]): T[] {
  const candidates = entries.filter((e) => !e.specificDate && specialTypesOf(e).length > 0);
  const childToday = EREV_YOM_TOV_CHILDREN.filter((t) => dayTypes.includes(t));
  const hasChildEntry = candidates.some((e) => e.dayTypes.some((t) => childToday.includes(t)));
  // A child entry exists for today: the generic Erev Yom Tov entries step aside.
  const matchTypes = hasChildEntry ? dayTypes.filter((t) => t !== 'erevYomTov') : dayTypes;
  const direct = candidates.filter((e) => specialTypesOf(e).some((t) => matchTypes.includes(t)));
  if (direct.length > 0) return direct;
  if (dayTypes.some((t) => (YOM_TOV_DAY_TYPES as HoursDayType[]).includes(t))) {
    return candidates.filter((e) => e.dayTypes.includes('yomTov'));
  }
  return [];
}

/** The day's zmanim plus "Shabbos ends" (the same as the day's Tzeit HaKochavim, see normalizeZmanim). */
export function withHoursZmanim(zmanim: ZmanimData): ZmanimData {
  if (!zmanim.Tzet) return zmanim;
  return {
    ...zmanim,
    [SHABBOS_ENDS_ZMAN]: zmanim.Tzet,
  } as ZmanimData;
}

function toRanges(entries: HoursEntry[], zmanim: ZmanimData): OpenRange[] {
  return entries
    .map((e) => {
      const open = resolveTimeRule(e.open, zmanim);
      const close = resolveTimeRule(e.close, zmanim);
      return {
        entryId: e.id,
        open,
        close,
        closesNextDay: toMinutes(close) < toMinutes(open),
        noteEn: e.noteEn,
        noteHe: e.noteHe,
      };
    })
    .filter((r) => r.open !== r.close)
    .sort((a, b) => toMinutes(a.open) - toMinutes(b.open));
}

/**
 * Resolve one date's hours. Exported for tests and the week view.
 * `nextDayZmanim` (tomorrow) tells Erev Yom Kippur / Erev Tisha B'Av apart.
 */
export function resolveDayHours(
  entries: HoursEntry[],
  date: string,
  dayZmanim: ZmanimData,
  nextDayZmanim?: ZmanimData
): DayHours {
  const dow = dayOfWeekOf(date);
  const dayTypes = hoursDayTypes(dayZmanim, nextDayZmanim);
  const zmanim = withHoursZmanim(dayZmanim);
  const base = { date, dayOfWeek: dow, dayTypes, callToCheck: false, mayDiffer: false };

  const fromEntries = (matched: HoursEntry[], source: DayHours['source'], extra: Partial<DayHours> = {}): DayHours => {
    if (matched.some((e) => e.closed)) {
      return { ...base, ranges: [], closed: true, source, ...extra, mayDiffer: false };
    }
    const ranges = toRanges(matched, zmanim);
    return { ...base, ranges, closed: ranges.length === 0, source, ...extra };
  };

  const active = (entries || []).filter((e) => inSeason(e, date));

  // 1. Specific date wins over everything.
  const specific = active.filter((e) => e.specificDate === date);
  if (specific.length > 0) return fromEntries(specific, 'specificDate');

  // 2. Hours the owner entered for one of today's special day types.
  const special = specialDayMatches(active, dayTypes);
  if (special.length > 0) return fromEntries(special, 'specialDay');

  // 3. Regular day-of-week hours.
  const regular = active.filter((e) => regularOn(e, dow));
  if (regular.length === 0) {
    return { ...base, ranges: [], closed: true, source: 'none' };
  }
  if (dayTypes.some((t) => (REST_DAY_TYPES as HoursDayType[]).includes(t))) {
    // Normally open today, but it's Yom Tov / Yom Kippur and no hours were entered.
    if (regular.some((e) => e.closed)) return fromEntries(regular, 'regular');
    return { ...base, ranges: [], closed: false, callToCheck: true, source: 'regular' };
  }
  const mayDiffer = dayTypes.some((t) => MAY_DIFFER_DAY_TYPES.includes(t));
  return fromEntries(regular, 'regular', { mayDiffer });
}

// ---------- status now ----------

interface AbsoluteRange {
  start: number;
  end: number;
  opensAt: DateTimePoint;
  closesAt: DateTimePoint;
}

/** Ranges as minutes relative to the start of `today` (yesterday's are negative). */
function absoluteRanges(day: DayHours, dayOffset: number): AbsoluteRange[] {
  return day.ranges.map((r) => {
    const start = dayOffset * 1440 + toMinutes(r.open);
    const end = (dayOffset + (r.closesNextDay ? 1 : 0)) * 1440 + toMinutes(r.close);
    return {
      start,
      end,
      opensAt: { date: day.date, time: r.open },
      closesAt: { date: r.closesNextDay ? addDays(day.date, 1) : day.date, time: r.close },
    };
  });
}

/**
 * Resolve a listing's opening hours: today's ranges, status now, next opening
 * and a 7-day week view.
 *
 * @param getZmanim zmanim for any 'YYYY-MM-DD' (the hook passes cache/Hebcal; tests pass Hebcal).
 * @param now the current Israel date and time — never read from the clock here.
 */
export function resolveOpeningHours(
  listing: { hours: HoursEntry[]; hoursMode: HoursMode },
  getZmanim: (date: string) => ZmanimData,
  now: DateTimePoint
): OpeningHoursResult {
  const entries = listing.hours || [];
  const dayAt = (offset: number) => {
    const date = addDays(now.date, offset);
    return resolveDayHours(entries, date, getZmanim(date), getZmanim(addDays(date, 1)));
  };

  const yesterday = dayAt(-1);
  const week = [0, 1, 2, 3, 4, 5, 6].map(dayAt);
  const today = week[0];

  if (listing.hoursMode === 'alwaysOpen') {
    return { status: 'open', today, week };
  }

  const nowMin = toMinutes(now.time);

  // Open right now? (Includes yesterday's ranges that run past midnight.)
  const current = [...absoluteRanges(yesterday, -1), ...absoluteRanges(today, 0)]
    .filter((r) => r.start <= nowMin && nowMin < r.end)
    .sort((a, b) => b.end - a.end)[0];

  // Next opening: later today, then the following days.
  let nextOpening: DateTimePoint | undefined;
  let minutesUntilOpen = Infinity;
  for (let offset = 0; offset <= NEXT_OPENING_SEARCH_DAYS && !nextOpening; offset++) {
    const day = offset < week.length ? week[offset] : dayAt(offset);
    const upcoming = absoluteRanges(day, offset).find((r) => r.start > nowMin);
    if (upcoming) {
      nextOpening = upcoming.opensAt;
      minutesUntilOpen = upcoming.start - nowMin;
    }
  }

  let status: OpenStatus;
  if (listing.hoursMode === 'byAppointment' || listing.hoursMode === 'callFirst') {
    status = 'callToCheck';
  } else if (current) {
    status = current.end - nowMin <= CLOSES_SOON_MINUTES ? 'closesSoon' : 'open';
  } else if (today.callToCheck) {
    status = 'callToCheck';
  } else if (minutesUntilOpen <= OPENS_SOON_MINUTES) {
    status = 'opensSoon';
  } else {
    status = 'closed';
  }

  return {
    status,
    today,
    ...(current ? { closesAt: current.closesAt } : nextOpening ? { nextOpening } : {}),
    week,
  };
}
